/**
 * Google Contacts (personal Gmail and Google Workspace) — API client for the Address Book /
 * Select Recipients modal. Backend: sendlinxapi GoogleContactsController (api/user/contacts/google/...).
 *
 * Same sign-in model as Microsoft Contacts (ms365Api.js): Google's consent screen opens in a popup
 * so the Send page is never reloaded; Google returns to /integrations/google/callback, which posts
 * {code, state} to the API with the user's normal auth headers and reports back (postMessage +
 * a localStorage event fallback). Google tokens and the client secret never reach the browser.
 * Contacts are synced into Sendlinx (read-only) and listed from the API.
 *
 * REACT_APP_GOOGLE_CONTACTS_MOCK=true (or REACT_APP_MS365_MOCK=true) runs the UI on sample data. Never in production.
 */
import axios from "axios";
import Config from "../../../../../Config";

export const GOOGLE_MESSAGE = "sendlinx:google-contacts";
export const GOOGLE_RESULT_KEY = "sendlinx_google_contacts_result";
export const GOOGLE_RETURN_KEY = "sendlinx_google_contacts_return";
export const GOOGLE_CALLBACK_PATH = "/integrations/google/callback";

export const isGoogleMockMode = () => {
    try{ return process.env.REACT_APP_GOOGLE_CONTACTS_MOCK === "true" || process.env.REACT_APP_MS365_MOCK === "true"; }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};
const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};
const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = { url: Config.getApiUrl()+path, method: method, headers: Config.getApiHeaders() };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/* ---------------- mock store ---------------- */
const MOCK_PEOPLE = [
    ["Ava Thompson", "ava.thompson@gmail.example", "Thompson Design"], ["Noah Patel", "noah.patel@brightway.example", "Brightway Ltd"],
    ["Mia Chen", "mia.chen@northwind.example", "Northwind"], ["Liam O'Brien", "liam@obrien.example", ""],
    ["", "billing@contoso.example", "Contoso"], ["Zara Ali", "zara.ali@gmail.example", ""]
];
const mock = {
    status: { provider: "google", configured: true, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null, sync: null },
    rows: (() => {
        const rows = MOCK_PEOPLE.map(([name, email, company], i) => ({ id: "g"+i, source: "google", sourceId: "people/c"+i, name,
            firstName: name.split(" ")[0] || "", lastName: name.split(" ").slice(1).join(" "), company, email, isPrimary: true, photoUrl: null }));
        for(let i = 1; i <= 60; i++){
            const n = String(i).padStart(2, "0");
            rows.push({ id: "gs"+i, source: "google", sourceId: "people/s"+i, name: "Google Sample "+n, firstName: "Google", lastName: "Sample "+n,
                company: i % 4 === 0 ? "Example Inc" : "", email: "gsample"+n+"@example.com", isPrimary: true, photoUrl: null });
        }
        return rows;
    })()
};
const copy = (v) => JSON.parse(JSON.stringify(v));
const delay = (v, ms = 250) => new Promise(resolve => setTimeout(() => resolve(copy(v)), ms));
const mockContacts = (query) => {
    const q = String(query.q || "").trim().toLowerCase();
    let rows = mock.rows.filter(r => !q || (r.name+" "+r.email+" "+r.company).toLowerCase().includes(q));
    const key = query.sort || "name";
    const dir = query.dir === "desc" ? -1 : 1;
    rows = rows.slice().sort((a, b) => {
        const av = key === "name" ? (a.name || a.email) : a[key];
        const bv = key === "name" ? (b.name || b.email) : b[key];
        if((av === "") !== (bv === "")){ return av === "" ? 1 : -1; }
        return av.localeCompare(bv) * dir || a.email.localeCompare(b.email);
    });
    const limit = query.limit || 50;
    const page = query.page || 1;
    return { items: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit, hasMore: page * limit < rows.length,
        syncedAt: mock.status.lastSyncedAt, syncing: false };
};
const mockSynced = () => {
    const now = new Date().toISOString();
    mock.status = { ...mock.status, lastSyncedAt: now, sync: { status: "idle", error: null, contactCount: mock.rows.length, lastAttemptAt: now } };
    return mock.status;
};
const notConnected = () => Promise.reject({ message: "Connect your Google account first.", reason: "not_connected" });

/* ---------------- API ---------------- */
const BASE = "user/contacts/google";

const GoogleContactsApi = {
    status(){
        if(isGoogleMockMode()){ return delay(mock.status, 150); }
        return request("GET", BASE+"/status");
    },
    /** Returns { authorizeUrl, expiresIn }. */
    connect(){
        if(isGoogleMockMode()){ return delay({ authorizeUrl: "about:blank", expiresIn: 600 }, 100); }
        return request("POST", BASE+"/connect", { data: {} });
    },
    /** Called by the callback page with Google's ?code&state (or ?error). Returns the new status (first sync included). */
    completeConnect({ code, state, error }){
        if(isGoogleMockMode()){ return mockConnect(); }
        return request("POST", BASE+"/callback", { data: { code: code || null, state: state || null, error: error || null } });
    },
    /** "Sync now". Returns status + { result }. */
    sync(full = false){
        if(isGoogleMockMode()){ return mock.status.connected ? delay({ ...mockSynced(), result: { started: true, alreadyRunning: false } }, 700) : notConnected(); }
        return request("POST", BASE+"/sync", { data: { full: !!full } });
    },
    /** query: { q, sort: "name"|"email"|"company", dir: "asc"|"desc", page, limit } */
    contacts(query = {}, source = null){
        if(isGoogleMockMode()){ return mock.status.connected ? delay(mockContacts(query), 300) : notConnected(); }
        const params = {};
        Object.keys(query).forEach(k => { if(query[k] !== "" && query[k] !== null && query[k] !== undefined){ params[k] = query[k]; } });
        return request("GET", BASE, { params, source });
    },
    disconnect(){
        if(isGoogleMockMode()){
            mock.status = { ...mock.status, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null, sync: null };
            return delay(mock.status);
        }
        return request("DELETE", BASE+"/connection");
    }
};

const mockConnect = () => {
    mock.status = { ...mock.status, status: "connected", connected: true, account: { email: "mattdec14@gmail.com", name: "" }, connectedAt: new Date().toISOString() };
    mockSynced();
    return delay({ ...mock.status, syncError: null }, 400);
};

/* ---------------- sign-in popup ---------------- */

/** Must be called directly from the click handler so browsers don't block it. Null when blocked. */
export const openGoogleSignInPopup = () => {
    if(isGoogleMockMode()){ return { mock: true, closed: false, close(){ this.closed = true; } }; }
    const width = 520;
    const height = 680;
    const left = Math.max(0, Math.round((window.screenX || 0) + ((window.outerWidth || width) - width) / 2));
    const top = Math.max(0, Math.round((window.screenY || 0) + ((window.outerHeight || height) - height) / 2));
    let popup = null;
    try{
        popup = window.open("", "sendlinx_google_signin", "popup=yes,width="+width+",height="+height+",left="+left+",top="+top);
    }catch(e){ popup = null; }
    if(!popup){ return null; }
    try{
        popup.document.title = "Connecting your Google account…";
        popup.document.body.style.cssText = "font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#444";
        popup.document.body.textContent = "Opening Google sign-in…";
    }catch(e){ }
    return popup;
};

const SIGN_IN_TIMEOUT_MS = 10 * 60 * 1000;

export const closeGoogleSignInPopup = (popup) => {
    try{ if(popup && !popup.mock && !popup.closed){ popup.close(); } }catch(e){ }
};

/**
 * Sends the popup to Google and resolves when the callback page reports back or the popup is
 * closed: { ok, message, status } or { closed: true } ({ closed: true, timedOut: true } after 10 minutes).
 */
export const runGoogleSignIn = (popup) => {
    if(popup && popup.mock){
        return mockConnect().then(status => ({ ok: true, status }));
    }
    return GoogleContactsApi.connect().then(data => {
        if(!popup || popup.closed){ return { closed: true }; }
        popup.location.href = data.authorizeUrl;
        return new Promise(resolve => {
            let done = false;
            const finish = (result) => {
                if(done){ return; }
                done = true;
                window.removeEventListener("message", onMessage);
                window.removeEventListener("storage", onStorage);
                clearInterval(timer);
                clearTimeout(timeout);
                if(result && result.type === GOOGLE_MESSAGE){ closeGoogleSignInPopup(popup); }
                resolve(result);
            };
            const onMessage = (event) => {
                if(event.origin !== window.location.origin || !event.data || event.data.type !== GOOGLE_MESSAGE){ return; }
                finish(event.data);
            };
            const onStorage = (event) => {
                if(event.key !== GOOGLE_RESULT_KEY || !event.newValue){ return; }
                try{ finish(JSON.parse(event.newValue)); }catch(e){ }
            };
            window.addEventListener("message", onMessage);
            window.addEventListener("storage", onStorage);
            const timer = setInterval(() => {
                let closed = true;
                try{ closed = popup.closed; }catch(e){ closed = false; }
                // The first import runs before the callback reports back; give it time after the popup closes.
                if(closed){ setTimeout(() => finish({ closed: true }), 400); clearInterval(timer); }
            }, 500);
            const timeout = setTimeout(() => { closeGoogleSignInPopup(popup); finish({ closed: true, timedOut: true }); }, SIGN_IN_TIMEOUT_MS);
        });
    }).catch(err => {
        try{ if(popup && !popup.closed){ popup.close(); } }catch(e){ }
        throw err;
    });
};

/** Popup blocked: sign in in this tab and come back afterwards. */
export const googleSignInSameTab = () => {
    return GoogleContactsApi.connect().then(data => {
        try{ sessionStorage.setItem(GOOGLE_RETURN_KEY, window.location.pathname || "/"); }catch(e){ }
        window.location.assign(data.authorizeUrl);
    });
};

export default GoogleContactsApi;
